import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { ToastService } from '@independent-software/typeui/controls/Toast';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Label } from '@independent-software/typeui/controls/Label';
import { Latitude, Longitude } from '../../controls/GIS';
import { Header } from '@independent-software/typeui/controls/Header';
import { Number } from '@independent-software/typeui/formatters/Number';

import { AuthContext } from '../../AuthContext';
import { FilterContext } from '../../FilterContext';
import { Section, Viewer } from '../../modules';
import { Station, StationFactory, Stationcategory, Sample, Result } from '../../resource/'; 
import { Output } from '../../controls/Output';
import { ListSamples } from '../Sample';
import { ListResults } from '../Result';
import { LocationMap } from '../../maps/LocationMap/LocationMap';

const ViewStation = () => {
  const auth = React.useContext(AuthContext);
  const filter = React.useContext(FilterContext);
  const navigate = useNavigate();
  const { id } = useParams();
  
  const handleEdit = () => {
    navigate(`/stations/edit/${parseInt(id)}`);
  }

  const handleDelete = () => {
    ToastService.toast("Station deleted.");
    navigate('/stations');
  }    

  const handleSampleClick = (item:Sample) => {
    navigate(`/samples/${item.id}`);
  }    

  const handleCategoryClick = (category: Stationcategory) => {
    navigate(`/stationcategories/${category.id}`);
  }

  const handleResultClick = (item: Result) => {
    navigate(`/results/${item.id}`);
  }  

  return (
    <Viewer<Station> 
      auth={auth}
      id={parseInt(id)}
      factory={StationFactory}
      canEdit='can_edit_stations'
      canDelete='can_delete_stations'
      onEdit={handleEdit}
      onDelete={handleDelete}
      content={(station: Station) => 
        <React.Fragment>
          <Section dark padded>
            <Header size="h2"><Label>{station.name}</Label> {station.description && <Label>{station.description}</Label>}</Header>
          </Section>
          <Tabs underlined>
            <Tabs.Pane label="Data">
              <Section padded="very">
                <Flex stackable>
                  <Flex.Row>
                    <Flex.Column width={1}>
                      <Output label="Name" hint="Station name or code">{station.name}</Output>
                    </Flex.Column>
                    <Flex.Column width={2}>
                      <Output label="Description" hint="Optional station description">{station.description}</Output>
                    </Flex.Column>
                  </Flex.Row>
                  <Flex.Row>
                    <Flex.Column width={3}>
                      <Output nobackground label="Categories">
                        {station.stationcategories.map(cat => 
                          <Label onClick={auth.hasRight('can_view_stationcategories') ?  () => handleCategoryClick(cat) : null} basic tag key={cat.id}>{cat.name}</Label>
                        )}
                      </Output>
                    </Flex.Column>
                  </Flex.Row>
                  <Flex.Row>
                    <Flex.Column width={1}>
                      <Output nobackground label="Latitude" hint="Station latitude"><Label><Number value={station.latitude} decimals={6}/></Label> <Label><Latitude value={station.latitude}/></Label></Output>
                    </Flex.Column>
                    <Flex.Column width={2}>
                      <Flex.Columns count={2}>
                        <Output nobackground label="Longitude" hint="Station longitude"><Label><Number value={station.longitude} decimals={6}/></Label> <Label><Longitude value={station.longitude}/></Label></Output>
                        <Output label="Depth" hint="Distance from tidal datum in meters">{station.depth}</Output>
                      </Flex.Columns>
                    </Flex.Column>
                  </Flex.Row>
                </Flex>
              </Section>
              <LocationMap lat={station.latitude} lng={station.longitude} zoom={11}/>
            </Tabs.Pane>
            <Tabs.Pane label={<span>Samples <Label size="small">{station.samples_count}</Label></span>}>
              <ListSamples auth={auth} filter={filter} name="station_samples" stationId={station.id} onClick={auth.hasRight('can_view_samples') && handleSampleClick}/>
            </Tabs.Pane>                 
            <Tabs.Pane label="Results">
              <ListResults stationId={station.id} name="station_results" auth={auth} filter={filter} onClick={auth.hasRight('can_view_results') && handleResultClick}/>
            </Tabs.Pane>              
          </Tabs>
        </React.Fragment>}>
    </Viewer>
  )
}

export { ViewStation }
